/**
 * Native window chrome that has to track the app theme. The macOS window uses an overlay title bar
 * (tauri.conf.json `titleBarStyle: "Overlay"`), so the NSWindow's own background is visible as a thin
 * strip at the very top, around/behind the traffic lights. Its default is white — invisible in light
 * mode but a bright stripe across the top in dark mode. We paint the window background to match the
 * resolved app surface so the strip disappears in both themes (and re-paint whenever the theme flips).
 *
 * Desktop-only and best-effort: on the web this is a no-op, and the Tauri window API is loaded via a
 * DYNAMIC import so the web bundle never pulls native plugin code (mirrors the rest of `shell/`).
 */
import { isTauri, isMobile } from './shell.js';

/** Parse a `#rgb` / `#rrggbb` string into an [r, g, b] byte triple, or null if it isn't hex. */
function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  const raw = m?.[1];
  if (!raw) return null;
  const h =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/**
 * Paint the desktop window background to `hex` (the resolved `--lapo-bg`). No-op on web and on the
 * mobile shells (which have no overlay title bar). Failures are swallowed — a mispainted 1px strip is
 * cosmetic and must never break theme application.
 */
export function applyDesktopWindowBackground(hex: string | undefined): void {
  if (!hex || !isTauri() || isMobile()) return;
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  void (async () => {
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      await getCurrentWindow().setBackgroundColor(rgb);
    } catch {
      /* window API unavailable or permission denied — leave the native default */
    }
  })();
}
